"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown, MapPin, Phone, Search, Mail, Share2 } from "lucide-react";
import Image from "next/image";
import { gsap } from "gsap";
import { useQuoteModal } from "@/context/QuoteModalContext";
import { useSettings } from "@/context/SettingsContext";
type NavLink = {
  name: string;
  href: string;
  hasDropdown?: boolean;
};

const navLinks: NavLink[] = [
  { name: "Home", href: "/" },
  { name: "Our Approach", href: "/our-approach" },
  { name: "Our Projects", href: "/our-projects" },
  { name: "Services", href: "/services" },
  { name: "Insights", href: "/insights" },
  { name: "Contact", href: "/contact" },
];

export default function Header() {
  const settings = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shouldRenderMenu, setShouldRenderMenu] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const isLightPage = pathname.startsWith("/insights") || pathname.startsWith("/services") || pathname.startsWith("/capabilities") || pathname.startsWith("/our-projects") || pathname.startsWith("/our-approach") || pathname.startsWith("/contact") || pathname.startsWith("/privacy-policy") || pathname.startsWith("/terms-and-conditions");
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Handle Entrance / Exit animations instead of AnimatePresence
  useEffect(() => {
    if (menuOpen) {
      setShouldRenderMenu(true);
    } else if (shouldRenderMenu) {
      if (backdropRef.current && drawerRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.3 });
        gsap.to(drawerRef.current, {
          x: "100%",
          duration: 0.4,
          ease: "power2.inOut",
          onComplete: () => setShouldRenderMenu(false)
        });
      } else {
        setShouldRenderMenu(false);
      }
    }
  }, [menuOpen, shouldRenderMenu]);

  useEffect(() => {
    if (shouldRenderMenu && menuOpen) {
      gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 });
      gsap.fromTo(drawerRef.current, { x: "100%" }, { x: "0%", duration: 0.5, ease: "power3.out" }); // power3 gives a nice snap similar to spring
    }
  }, [shouldRenderMenu, menuOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
        {/* ════ TOP BAR ════ */}
        <div className={`hidden lg:flex w-full bg-[#1c1c1c] text-gray-300 px-8 sm:px-12 items-center justify-between text-[13px] transition-all duration-300 overflow-hidden ${scrolled ? 'max-h-0 py-0 opacity-0 border-none' : 'max-h-[80px] py-3 opacity-100 border-b border-white/10'}`}>
          <div className="flex items-center gap-8 font-medium">
            <a href={`https://maps.google.com/?q=${encodeURIComponent(settings.address || '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer">
              <MapPin className="w-4 h-4 text-[#0d9488]" />
              <span className="line-clamp-1 max-w-[250px]">{settings.address}</span>
            </a>
            <a href={`mailto:${settings.contactEmail}`} className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer">
              <Mail className="w-4 h-4 text-[#0d9488]" />
              <span>{settings.contactEmail}</span>
            </a>
            {settings.contactPhone?.trim() && (
              <a href={`tel:${settings.contactPhone}`} className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer">
                <Phone className="w-4 h-4 text-[#0d9488]" />
                <span>{settings.contactPhone}</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 text-white">
              {/* Social Icons */}
              {settings.socialLinks?.facebook && (
                <a href={settings.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <svg className="w-3.5 h-3.5 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z" /></svg>
                </a>
              )}
              {settings.socialLinks?.twitter && (
                <a href={settings.socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                  <svg className="w-4 h-4 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.05c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" /></svg>
                </a>
              )}
              {settings.socialLinks?.instagram && (
                <a href={settings.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <svg className="w-3.5 h-3.5 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                </a>
              )}
              {settings.socialLinks?.linkedin && (
                <a href={settings.socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                  <svg className="w-3.5 h-3.5 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                </a>
              )}
              {settings.socialLinks?.youtube && (
                <a href={settings.socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                  <svg className="w-3.5 h-3.5 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                </a>
              )}
              {settings.socialLinks?.whatsapp && (
                <a href={settings.socialLinks.whatsapp.startsWith('http') ? settings.socialLinks.whatsapp : `https://wa.me/${settings.socialLinks.whatsapp.replace(/[^0-9]/g, '')}?text=Hello%20Compare%20All%2C%20I%20would%20like%20to%20learn%20more%20about%20your%20services`} target="_blank" rel="noopener noreferrer">
                  <svg className="w-3.5 h-3.5 cursor-pointer hover:text-[#0d9488] transition-colors fill-current" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>
                </a>
              )}
            </div>
            <button onClick={openModal} className="bg-[#0f766e] hover:bg-[#115e59] text-white px-6 py-2 rounded-full font-bold transition-colors">
              Quote Me
            </button>
          </div>
        </div>

        {/* ════ MAIN HEADER ════ */}
        <div className={`w-full mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between transition-all duration-300 ${scrolled ? "bg-white shadow-md py-2" : "bg-transparent py-3 md:py-5"}`}>

          {/* Logo (Fully Responsive) */}
          <Link href="/" className="flex items-center relative z-50 group">
            {(!scrolled && !isLightPage) && (
              <div
                className="absolute -inset-y-1 -left-6 -right-10 bg-gradient-to-r from-white/95 via-white/90 to-white/60 backdrop-blur-md shadow-2xl -z-10 transition-transform duration-500 group-hover:scale-[1.02]"
                style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-2 bg-[#0d9488]"></div>
              </div>
            )}
            <Image
              src="/originalLogoTransparent.png"
              alt="Compare All Logo"
              width={240}
              height={120}
              priority
              sizes="(max-width: 768px) 192px, 240px"
              className={`w-auto max-w-[65vw] sm:max-w-none object-contain relative z-10 transition-all duration-500 ${scrolled
                ? 'h-10 sm:h-12 md:h-14 lg:h-14'
                : 'h-12 sm:h-16 md:h-20 lg:h-24'
                }`}
            />
          </Link>

          {/* Desktop Right Group: Nav + Search */}
          <div className="hidden lg:flex items-center gap-10">
            <nav className="flex items-center gap-8">
              {navLinks.map((link) => {
                const isActive = pathname === link.href || (link.href !== "/" && pathname.startsWith(link.href));

                const linkColor = scrolled
                  ? (isActive ? 'text-gray-900 font-bold' : 'text-gray-600 hover:text-[#0d9488]')
                  : (isActive
                    ? (isLightPage ? 'text-gray-900 font-bold' : 'text-white font-bold')
                    : (isLightPage ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white')
                  );
                const indicatorColor = scrolled ? 'bg-[#0d9488]' : (isLightPage ? 'bg-gray-900' : 'bg-white');

                return (
                  <Link key={link.name} href={link.href} className={`flex items-center gap-1 text-[15px] font-sans transition-colors relative group ${linkColor}`}>
                    {link.name}
                    {link.hasDropdown && <ChevronDown className="w-3.5 h-3.5 mt-0.5 opacity-70" />}
                    {isActive && <span className={`absolute -bottom-2 left-0 h-[2px] w-5 transition-colors ${indicatorColor}`}></span>}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Mobile Right Edge Icons */}
          <div className="flex lg:hidden items-center gap-3 relative z-50">
            <button
              onClick={() => setMenuOpen(true)}
              className={`p-2 transition-colors ${scrolled ? 'text-gray-900' : (isLightPage ? 'text-gray-900' : 'text-white')}`}
            >
              <Menu className="w-6 h-6 stroke-[2]" />
            </button>
          </div>
        </div>
      </header>

      {/* ════ MOBILE RIGHT-SIDE SLIDE DRAWER ════ */}
      {shouldRenderMenu && (
        <>
          {/* Backdrop */}
          <div
            ref={backdropRef}
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] lg:hidden"
            style={{ opacity: 0 }}
          />

          {/* Drawer */}
          <div
            ref={drawerRef}
            className="fixed top-0 right-0 bottom-0 w-[300px] max-w-[85vw] bg-white z-[70] shadow-2xl flex flex-col lg:hidden overflow-y-auto"
            style={{ transform: "translateX(100%)" }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
              <span className="font-serif font-bold text-lg text-gray-900">Menu</span>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 -mr-2 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col flex-1 overflow-y-auto">
              <div className="pt-6 px-6 pb-8">
                <nav className="flex flex-col gap-6">
                  {navLinks.map((link) => {
                    const isActive = pathname === link.href || (link.href !== "/" && pathname.startsWith(link.href));
                    return (
                      <Link
                        key={link.name}
                        href={link.href}
                        onClick={() => setMenuOpen(false)}
                        className={`text-[15px] font-sans transition-colors relative group w-fit ${isActive ? "text-gray-900 font-bold" : "text-gray-600 hover:text-[#0d9488]"
                          }`}
                      >
                        {link.name}
                        {isActive && <span className="absolute -bottom-2 left-0 h-[2px] w-5 transition-colors bg-[#0d9488]"></span>}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              <div className="mt-auto bg-[#1c1c1c] text-gray-300 p-6 flex flex-col gap-6 shrink-0 shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
                  <button 
                    onClick={() => { setMenuOpen(false); openModal(); }} 
                    className="bg-[#0f766e] hover:bg-[#115e59] text-white w-full px-6 py-3 rounded-full font-bold transition-colors"
                  >
                    Quote Me
                  </button>

                <div className="flex flex-col gap-4 text-[13px] font-medium">
                  {settings.contactPhone?.trim() && (
                    <a href={`tel:${settings.contactPhone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                      <Phone className="w-4 h-4 text-[#0d9488]" /> {settings.contactPhone}
                    </a>
                  )}
                  <a href={`mailto:${settings.contactEmail}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="w-4 h-4 text-[#0d9488]" /> {settings.contactEmail}
                  </a>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(settings.address || '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer">
                    <MapPin className="w-4 h-4 text-[#0d9488]" /> {settings.address}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
